import isEqual from 'lodash.isequal';
import {
  ButtonPrimary,
  ButtonPseudo,
  ButtonSecondary,
  ButtonSize,
  formatBalance,
  IconBlock,
  IconDownload,
  IconEye,
  IconEyeOff,
  IconSelected,
  IconSettings,
  SelectItemProps,
  SidePanel,
  SidePanelType,
} from '@ui/src';
import { AccountItemTypes } from './types';
import Card from '../card/card';
import AccountSettingsPanel from './settings-panel';
import AccountSettingsPopup from './settings-popup';
import { CustomerStatus } from '../../../hoc/common/types';

import cn from './styles.module.css';

const AccountItem = ({
  balance,
  baseCurrency,
  currencies,
  account,
  currenciesList,
  overdraftEnabled,
  accountOverdraftEnabled,
  setShowCurrencies,
  setTopUpPanelIsOpen,
  setStatementPanelVisible,
  setSelectedAccount,
  setCurrenciesList,
  setSettingsPanelVisible,
  settingsPanelVisible,
  setSelectedTab,
  setOverdraftEnabled,
  setBalances,
  setDefaultBalances,
  setClosingBalancesAt,
  clearSettingsPanel,
  organizationStatus,
  updateVisibilityInProgress,
  updateOverdraftBalanceIsLoading,
  visibleToClient,
  onUpdateVisibility,
  defaultBalances,
  accountsBalances,
  onUpdateOverdraftBalanceValue,
  selectedTab,
  creditBlock,
  setCreditBlock,
  setBlockTopupPopupVisible,
  debitBlock,
  setDebitBlock,
  setBlockWithdrawPopupVisible,
  setVisibleToClient,
  closingBalancesAt,
  getCustomerAccountClosingBalancesAction,
  clearClosingBalances,
  closedBalances,
  onUpdateOverdraftStatus,
  overdraftUpdateInProgress,
  balancesIsLoading,
  balancesIsFetching,
  closed,
  setClosePopupVisible,
  activePermissions,
  // selectedAccountId,
  isLoading,
  accountsLogs,
  users,
  accountsLogsPagination,
  isFetching,
  getChangeLogsAction,
  onBlockWithdraw,
  blockWithdrawInProgress,
  blockWithdrawPopupVisible,
  blockUblockWithdrawalsError,
  onBlockTopup,
  blockTopupInProgress,
  blockTopupPopupVisible,
  blockUblockTopupError,
  setComment,
  closePopupVisible,
  comment,
  closeOpenError,
  closeInProgress,
  onClose,
  selectedAccount,
  ffSettings,
}: AccountItemTypes) => {
  const baseCurrencyBalance = balance?.find(
    (item) => item.currencyId === baseCurrency?.id,
  );

  const balanceWithCurrencyName = balance?.map((item) => ({
    ...item,
    currencyName:
      currencies?.find((currency) => item.currencyId === currency.value)
        ?.suffix ||
      item?.currency?.name ||
      '',
  }));

  let status;

  if (account) {
    if (account.closed) {
      status = 'Account closed';
    } else if (!account.closed && account?.debitBlock && account.creditBlock) {
      status = 'Account blocked';
    } else if (!account.closed && !account?.debitBlock && account.creditBlock) {
      status = 'Blocked for top-up';
    } else if (!account.closed && account?.debitBlock && !account.creditBlock) {
      status = 'Withdrawal blocked';
    } else if (
      !account.closed &&
      !account?.debitBlock &&
      !account.creditBlock
    ) {
      if (account?.active) {
        status = 'Active';
      } else {
        status = 'Inactive';
      }
    }
  }

  return (
    <Card className={cn.accountCard}>
      <div className={cn.accountItemTop}>
        <div className={cn.accountItemTitleWrapper}>
          <div className={cn.accountItemTitle}>
            {account?.fullNumber}
            <div className={cn.accountCreatedDate}>
              {`created on ${new Date(
                account?.createdAt as string,
              ).toLocaleDateString()}`}
            </div>
          </div>
          <div className={cn.accountTotal} style={{ display: 'none' }}>
            {`${formatBalance(
              account?.totalBalanceBC || 0,
              currenciesList?.find((item) => item.id === baseCurrency?.id),
            )} ${baseCurrency?.name}`}
            <span className={cn.accountTotalHint}>bc</span>
          </div>
        </div>
        <div>
          <div className={cn.accountStatus}>
            {overdraftEnabled &&
              account?.overdraftEnabled &&
              overdraftEnabled && (
                <div className={cn.overdraftStatus}>Overdraft</div>
              )}
            <div
              className={`${cn.accountStatusValue} ${
                !account?.active ||
                account.debitBlock ||
                account.creditBlock ||
                account.closed
                  ? cn.accountStatusInactive
                  : ''
              }`}
            >
              {status === 'Active' ? (
                <div className={cn.iconWrapper}>
                  <IconSelected className={cn.statusIcon} />
                </div>
              ) : (
                <div className={cn.iconWrapper}>
                  <IconBlock className={cn.statusIcon} />
                </div>
              )}
              <span>{status}</span>
            </div>
            <div
              className={`${cn.accountStatusValue} ${
                !account?.visibleToClient ? cn.accountStatusInactive : ''
              } ${cn.accountVisibility}`}
            >
              {account?.visibleToClient ? (
                <div className={cn.iconWrapper}>
                  <IconEye width={16} height={16} className={cn.statusIcon} />
                </div>
              ) : (
                <div className={cn.iconWrapper}>
                  <IconEyeOff
                    width={16}
                    height={16}
                    className={cn.statusIcon}
                  />
                </div>
              )}
              <span>
                {account?.visibleToClient ? 'Visible' : 'Not Visible'}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className={cn.accountCurrenciesWrapper}>
        {baseCurrencyBalance && (
          <div className={cn.accountCurrency}>
            <div className={cn.accountCurrencyName}>
              {`${
                currencies?.find(
                  (currency) =>
                    baseCurrencyBalance.currencyId === currency.value,
                )?.suffix
              }:`}
            </div>
            {formatBalance(
              baseCurrencyBalance.value,
              currenciesList?.find(
                (item) => item.id === baseCurrencyBalance.currencyId,
              ),
            )}
            {baseCurrencyBalance?.overdraftEnabled &&
              overdraftEnabled &&
              accountOverdraftEnabled && (
                <div className={cn.overdraftValue}>
                  {formatBalance(
                    baseCurrencyBalance.overdraftValue as number,
                    currenciesList?.find(
                      (item) => item.id === baseCurrencyBalance.currencyId,
                    ),
                  )}
                </div>
              )}
          </div>
        )}
        {balanceWithCurrencyName
          ?.filter(
            (item) => item.currencyId !== baseCurrencyBalance?.currencyId,
          )
          .sort((a, b) => a?.currencyName?.localeCompare(b?.currencyName))
          .map((item) => (
            <div className={cn.accountCurrency} key={item.id}>
              <div className={cn.accountCurrencyName}>
                {`${item.currencyName}:`}
              </div>
              {formatBalance(
                item.value,
                currenciesList?.find(
                  (currency) => currency.id === item.currencyId,
                ),
              )}
              {item?.overdraftEnabled &&
                overdraftEnabled &&
                accountOverdraftEnabled && (
                  <div className={cn.overdraftValue}>
                    {formatBalance(
                      item.overdraftValue as number,
                      currenciesList?.find(
                        (currency) => currency.id === item.currencyId,
                      ),
                    )}
                  </div>
                )}
            </div>
          ))}
      </div>
      <div className={cn.accountItemBottom}>
        <ButtonPseudo
          text="Available currencies"
          size={ButtonSize.SMALL}
          className={`${cn.currenciesButton} button-white`}
          icon={
            <IconSettings width={16} height={16} className={cn.settingsIcon} />
          }
          onClick={() => {
            setShowCurrencies(true);
            setSelectedAccount(account?.id as string);
          }}
        />
        <div className={cn.accountItemControls}>
          <ButtonPseudo
            text="Top up options"
            size={ButtonSize.SMALL}
            className={`${cn.topUpButton} ${cn.currenciesButton} button-white`}
            icon={
              <IconSettings
                width={16}
                height={16}
                className={cn.settingsIcon}
              />
            }
            onClick={() => {
              setSelectedAccount(account?.id as string);
              setTopUpPanelIsOpen(true);
            }}
          />
          <ButtonPseudo
            text="Statement"
            size={ButtonSize.SMALL}
            className={`${cn.withMargin} ${cn.downloadStatementButton} ${cn.currenciesButton} button-white`}
            icon={<IconDownload />}
            onClick={() => {
              setStatementPanelVisible(true);
              setSelectedAccount(account?.id as string);

              setCurrenciesList(
                balanceWithCurrencyName?.map((item) => ({
                  name: item.currencyName,
                  value: item.currencyId,
                })) as SelectItemProps[],
              );
            }}
          />
          <ButtonSecondary
            text="Settings"
            size={ButtonSize.SMALL}
            className={`${cn.currenciesButton} button-white`}
            onClick={() => {
              setSettingsPanelVisible(true);
              setSelectedAccount(account?.id as string);
            }}
          />
        </div>
      </div>
      <SidePanel
        isOpen={settingsPanelVisible}
        title="Account settings"
        type={SidePanelType.Accounts}
        onClose={() => {
          setSettingsPanelVisible(false);
          setSelectedAccount('');
          setSelectedTab('settings');
          setOverdraftEnabled(false);
          setBalances(undefined);
          setDefaultBalances(undefined);
          setClosingBalancesAt('');

          clearSettingsPanel();
        }}
        sidepanelExpandedKey="individual-account-settings"
        footer={
          <>
            {organizationStatus !== CustomerStatus.CLOSED && (
              <ButtonPrimary
                text={
                  updateVisibilityInProgress || updateOverdraftBalanceIsLoading
                    ? 'Loading...'
                    : 'Save'
                }
                onClick={() => {
                  if (account?.visibleToClient !== visibleToClient) {
                    onUpdateVisibility({
                      id: account?.id as string,
                      visibleToClientValue: visibleToClient,
                    });
                  }

                  if (!isEqual(defaultBalances, accountsBalances)) {
                    onUpdateOverdraftBalanceValue(account?.id as string);
                  }
                }}
                disabled={
                  isEqual(defaultBalances, accountsBalances) &&
                  account?.visibleToClient === visibleToClient
                }
                bold
              />
            )}

            <ButtonSecondary
              text="Cancel"
              onClick={() => {
                setSettingsPanelVisible(false);
                setSelectedAccount('');
                setSelectedTab('settings');
                setOverdraftEnabled(false);
                setBalances(undefined);
                setDefaultBalances(undefined);
                setClosingBalancesAt('');
                clearSettingsPanel();
              }}
              bold
            />
          </>
        }
        closeOnClickOutside
      >
        <AccountSettingsPanel
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          account={account}
          creditBlock={creditBlock}
          setCreditBlock={setCreditBlock}
          setBlockTopupPopupVisible={setBlockTopupPopupVisible}
          organizationStatus={organizationStatus}
          debitBlock={debitBlock}
          setDebitBlock={setDebitBlock}
          setBlockWithdrawPopupVisible={setBlockWithdrawPopupVisible}
          visibleToClient={visibleToClient}
          setVisibleToClient={setVisibleToClient}
          closingBalancesAt={closingBalancesAt}
          setClosingBalancesAt={setClosingBalancesAt}
          getCustomerAccountClosingBalancesAction={
            getCustomerAccountClosingBalancesAction
          }
          clearClosingBalances={clearClosingBalances}
          closedBalances={closedBalances}
          overdraftEnabled={overdraftEnabled}
          accountOverdraftEnabled={accountOverdraftEnabled}
          onUpdateOverdraftStatus={onUpdateOverdraftStatus}
          overdraftUpdateInProgress={overdraftUpdateInProgress}
          balancesIsLoading={balancesIsLoading}
          balancesIsFetching={balancesIsFetching}
          baseCurrencyBalance={baseCurrencyBalance}
          currencies={currencies}
          currenciesList={currenciesList}
          accountsBalances={accountsBalances}
          setBalances={setBalances}
          updateOverdraftBalanceIsLoading={updateOverdraftBalanceIsLoading}
          defaultBalances={defaultBalances}
          balanceWithCurrencyName={balanceWithCurrencyName}
          closed={closed}
          setClosePopupVisible={setClosePopupVisible}
          activePermissions={activePermissions}
          selectedAccount={selectedAccount}
          isLoading={isLoading}
          accountsLogs={accountsLogs}
          users={users}
          accountsLogsPagination={accountsLogsPagination}
          isFetching={isFetching}
          getChangeLogsAction={getChangeLogsAction}
          ffSettings={ffSettings}
        />
      </SidePanel>
      <AccountSettingsPopup
        debitBlock={debitBlock}
        setBlockWithdrawPopupVisible={setBlockWithdrawPopupVisible}
        onBlockWithdraw={onBlockWithdraw}
        account={account}
        blockWithdrawInProgress={blockWithdrawInProgress}
        blockWithdrawPopupVisible={blockWithdrawPopupVisible}
        blockUblockWithdrawalsError={blockUblockWithdrawalsError}
        creditBlock={creditBlock}
        setBlockTopupPopupVisible={setBlockTopupPopupVisible}
        onBlockTopup={onBlockTopup}
        blockTopupInProgress={blockTopupInProgress}
        blockTopupPopupVisible={blockTopupPopupVisible}
        blockUblockTopupError={blockUblockTopupError}
        closed={closed}
        setClosePopupVisible={setClosePopupVisible}
        setComment={setComment}
        closePopupVisible={closePopupVisible}
        comment={comment}
        closeOpenError={closeOpenError}
        closeInProgress={closeInProgress}
        onClose={onClose}
        setDebitBlock={setDebitBlock}
        setCreditBlock={setCreditBlock}
      />
    </Card>
  );
};

export default AccountItem;
